import { yupResolver } from "@hookform/resolvers/yup";
import {
	Button,
	designTokens,
	Flex,
	FormControl,
	FormControlText,
	FormGroup,
	Input,
	InputCheckBox,
	InputRadio,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import type { CompanyVendor, CompanyVendorEdit } from "~/models/CompanyVendor";
import { validationSchemaCompanyVendor } from "~/models/CompanyVendor";
import { VENDOR_COMPANY_ITANDI_ID } from "~/models/VendorCompany";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/CompanyVendor";
import { generatePath } from "~/utils/generatePath";

type Props = {
	companyVendor?: CompanyVendor | null;
	onClose: () => void;
};

export function CompanyVendorModal({ companyVendor = null, onClose }: Props) {
	const { mutate: globalMutate } = useSWRConfig();

	const defaultValues: CompanyVendorEdit = companyVendor ?? {
		completionReportImageRequired: true,
		defaultDealManagementType: "with_vendor",
		defaultEstimationBy: "vendor",
		email: "",
		inspectionReportImageRequired: true,
		name: "",
		planType: "saas",
		showTenantDefault: true,
		vendorCompany: null,
		vendorKey: ""
	};

	const isOmakasePlan = companyVendor?.planType === "omakase";

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isDirty },
		control
	} = useForm<CompanyVendorEdit & { invite: boolean }>({
		defaultValues: { ...defaultValues, invite: false },
		mode: "onSubmit",
		resolver: yupResolver(validationSchemaCompanyVendor)
	});

	const inviteValue = useWatch({ control, name: "invite" });

	const handleSave = async (
		submitValue: CompanyVendorEdit & { invite: boolean }
	): Promise<void> => {
		const result =
			companyVendor === null
				? await create(submitValue, submitValue.invite)
				: await update(submitValue, companyVendor.id);
		if (isSuccess(result)) {
			await globalMutate(generatePath("/company_vendors"));
			onClose();
			toast.success("保存しました。");
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	};

	return (
		<Modalv2 isOpen onClose={onClose} size="l">
			<ModalHeader title={companyVendor ? "施工会社編集" : "施工会社追加"} />
			<ModalBody>
				<FormGroup variant="vertical">
					<FormControl chip="必須" isInvalid={!!errors.name} label="施工会社名">
						<Input {...register("name")} disabled={isOmakasePlan} />
						<FormControlText
							error={errors.name?.message}
							help={
								isOmakasePlan
									? "修繕おまかせサービス依頼用のシステム名称のため、変更することはできません。"
									: undefined
							}
						/>
					</FormControl>
					{companyVendor === null && (
						<FormControl label="システム招待">
							<Controller
								control={control}
								name="invite"
								render={({ field }) => (
									<Flex gap={designTokens["spacing/xxs"]}>
										<InputRadio
											checked={!field.value}
											label="今はしない"
											onChange={(): void => {
												field.onChange(false);
											}}
											value="false"
										/>
										<InputRadio
											checked={field.value}
											label="施工会社をシステムに招待する"
											onChange={(): void => {
												field.onChange(true);
											}}
											value="true"
										/>
									</Flex>
								)}
							/>
							<FormControlText
								help={[
									"連携すると、報告画像の共有や見積書の承認、チャットなどをシステム上で行えます",
									"代表者メールアドレス宛に招待メールが送信され、施工会社様がアカウントを作成することで連携が完了します"
								]}
							/>
						</FormControl>
					)}
					<FormControl
						chip={inviteValue ? "必須" : undefined}
						isInvalid={!!errors.email}
						label="代表者メールアドレス"
					>
						<Input {...register("email")} />
						<FormControlText error={errors.email?.message} />
					</FormControl>
					<FormControl isInvalid={!!errors.vendorKey} label="施工会社管理番号">
						<Input {...register("vendorKey")} />
						<FormControlText error={errors.vendorKey?.message} />
					</FormControl>
					{companyVendor?.vendorCompany?.id !== VENDOR_COMPANY_ITANDI_ID && (
						<FormControl label="デフォルトの個人情報公開">
							<Controller
								control={control}
								name="showTenantDefault"
								render={({ field }) => (
									<InputCheckBox
										checked={field.value}
										label="依頼時にデフォルトで個人情報を公開する"
										onChange={(e): void => {
											field.onChange(e.target.checked);
										}}
									/>
								)}
							/>
						</FormControl>
					)}
					<FormControl label="デフォルトの見積作成者">
						<Controller
							control={control}
							name="defaultEstimationBy"
							render={({ field }) => (
								<InputCheckBox
									checked={field.value === "company"}
									disabled={companyVendor?.vendorEstimationRequired}
									label="依頼時にデフォルトで管理会社にて見積作成を行うようにする"
									onChange={(e): void => {
										field.onChange(e.target.checked ? "company" : "vendor");
									}}
								/>
							)}
						/>
						<FormControlText
							help={[
								"非共有の施工会社には、この設定は適用されません",
								companyVendor?.vendorEstimationRequired
									? "この施工会社はシステム上見積入力が必須に設定されています"
									: ""
							]}
						/>
					</FormControl>
					<FormControl label="案件の管理タイプ">
						<Controller
							control={control}
							name="defaultDealManagementType"
							render={({ field }) => (
								<InputCheckBox
									checked={field.value === "own"}
									disabled={companyVendor?.managementRequiredByVendor}
									label="アカウント連携している会社との案件を非共有にする"
									onChange={(e): void => {
										field.onChange(e.target.checked ? "own" : "with_vendor");
									}}
								/>
							)}
						/>
						{companyVendor?.managementRequiredByVendor ? (
							<FormControlText
								help={
									"この施工会社は案件を非共有として登録することを許可していません"
								}
							/>
						) : null}
					</FormControl>
					<FormControl label="完了報告画像の登録">
						<Controller
							control={control}
							name="completionReportImageRequired"
							render={({ field }) => (
								<InputCheckBox
									checked={field.value}
									disabled={
										companyVendor?.completionReportImageRequiredByVendor
									}
									label="アカウント連携している施工会社の完了報告画像の登録を必須にする"
									onChange={(e): void => {
										field.onChange(e.target.checked);
									}}
								/>
							)}
						/>
						{companyVendor?.completionReportImageRequiredByVendor ? (
							<FormControlText
								help={
									"この施工会社は完了報告画像の登録を任意とすることを許可していません"
								}
							/>
						) : null}
					</FormControl>
					<FormControl label="立会報告画像の登録">
						<Controller
							control={control}
							name="inspectionReportImageRequired"
							render={({ field }) => (
								<InputCheckBox
									checked={field.value}
									disabled={
										companyVendor?.inspectionReportImageRequiredByVendor
									}
									label="アカウント連携している施工会社の立会報告画像の登録を必須にする"
									onChange={(e): void => {
										field.onChange(e.target.checked);
									}}
								/>
							)}
						/>
						{companyVendor?.inspectionReportImageRequiredByVendor ? (
							<FormControlText
								help={
									"この施工会社は立会報告画像の登録を任意とすることを許可していません"
								}
							/>
						) : null}
					</FormControl>
				</FormGroup>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button disabled={isSubmitting} onClick={onClose} variant="secondary">
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={!isDirty}
						loading={isSubmitting}
						onClick={handleSubmit(handleSave)}
						variant="primary"
					>
						保存
					</Button>
				}
			/>
		</Modalv2>
	);
}
